import { createReducer, on } from '@ngrx/store';
import escapeStringRegexp from 'escape-string-regexp';
import { ItemSelectorActions } from '.';
import { ListItemCategoryDto } from 'src/app/services/api.services';
import { createSearchPattern, escapeHtml, isNullOrUndefined, isWhitespaceOnly } from 'src/app/shared/utils';
import { createItemSelectorActions } from './actions';

export interface IItemSelectorItem {
  id: number;

  categoryIds: number[];

  name: string;
  favourite: boolean;

  hiddenByStarredOnly: boolean;

  highlightedName: string;
  hiddenByFilter: boolean;

  hidden: boolean;

  subtitle: string;
};

export interface IItemSelectorCategory<I extends IItemSelectorItem> {
  id: number;

  name: string;
  alwaysVisible: boolean;

  hidden: boolean;
};

export interface ItemSelectorState<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>> {
  items: I[];
  categories: C[];

  selectedCategoryIndex: number;
  selectedCategory: C;
  selectedCategoryItems: I[];
  selectedItemIndex: number;
  selectedItem: I;

  starredOnly: boolean;
  filter: string;
};

export function createItemSelectorState<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>>(): ItemSelectorState<I, C> {
  return {
    items: [],
    categories: [],

    selectedCategoryIndex: -1,
    selectedCategory: null,
    selectedCategoryItems: [],
    selectedItemIndex: -1,
    selectedItem: null,

    starredOnly: null,
    filter: null
  };
}

export function createItemSelectorReducer<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>>(itemSelectorName: string, initialState_?: any, actions_?: any) {
  const TState = () => createItemSelectorState<I, C>();
  const TActions = () => createItemSelectorActions<I, C>(itemSelectorName);

  const actions = <ReturnType<typeof TActions>>(actions_ ?? TActions());
  const initialState = <ReturnType<typeof TState>>(initialState_ ?? TState());

  function getSelectedItem(state: ItemSelectorState<I, C>) {
    if (state.selectedItemIndex === -1)
      return null;

    return state.selectedCategoryItems[state.selectedItemIndex];
  }

  return createReducer(
    initialState,


    on(actions.setItemsAndCategories, (state, { items, categories }) => {
      return {
        ...state,
        items,
        categories,
      };
    }),


    on(actions.setInitialCategory, actions.setSelectedCategory, (state, action: { type: string, categoryIndex?: number }) => {
      let categoryIndex;

      if (action.categoryIndex) {
        categoryIndex =
          (isNullOrUndefined(action.categoryIndex) || (action.categoryIndex < 0) || (action.categoryIndex >= state.categories.length))
            ? -1 : action.categoryIndex;
      }
      else {
        categoryIndex = state.categories.findIndex(c => !c.hidden);
      }

      let categoryItems: I[] = [];
      let firstVisibleItemIndex = -1;

      if (categoryIndex !== -1) {
        let categoryId = state.categories[categoryIndex].id;

        categoryItems = state.items.filter(i => i.categoryIds.find(id => id === categoryId));

        firstVisibleItemIndex = categoryItems.findIndex(i => !i.hidden) ?? -1;
      }

      state = {
        ...state,
        selectedCategoryIndex: categoryIndex,
        selectedCategory: state.categories[categoryIndex],
        selectedCategoryItems: categoryItems,
        selectedItemIndex: firstVisibleItemIndex,
      };

      state.selectedItem = getSelectedItem(state);

      return state;
    }),

    on(actions.setSelectedItem, (state, { itemIndex }) => {
      itemIndex =
        (isNullOrUndefined(itemIndex) || (itemIndex < 0) || (itemIndex >= state.selectedCategoryItems.length))
          ? -1 : itemIndex;

      state = {
        ...state,
        selectedItemIndex: itemIndex,
      };

      state.selectedItem = getSelectedItem(state);

      return state;
    }),


    on(actions.updateVisibility, (state, { starredOnlyChanged, filterChanged }) => {
      let itemsChanged = false;

      let items = [...state.items];

      if (starredOnlyChanged) {
        items = items.map(item => {
          let hiddenByStarredOnly = state.starredOnly && !item.favourite;

          if (hiddenByStarredOnly === item.hiddenByStarredOnly)
            return item;

          itemsChanged = true;

          return {
            ...item,
            hiddenByStarredOnly
          }
        });
      }

      if (filterChanged) {
        if (state.filter) {
          let pattern = createSearchPattern(state.filter);

          items = items.map(item => {
            let anyMatches = false;

            let highlightedName = item.name.replace(pattern, m => {
              anyMatches = true;

              return `<span class="search-highlight">${escapeHtml(m)}</span>`;
            });

            let hiddenByFilter = false;

            if (anyMatches) {
              hiddenByFilter = false;
            }
            else {
              highlightedName = null;
              hiddenByFilter = true;
            }

            if (hiddenByFilter && item.hiddenByFilter)
              return item;

            itemsChanged = true;

            return {
              ...item,
              highlightedName,
              hiddenByFilter
            };
          });
        } else {
          items = items.map(item => {
            if (!item.hiddenByFilter)
              return item;

            itemsChanged = true;

            return {
              ...item,
              hiddenByFilter: false
            };
          });
        }
      }

      if (!itemsChanged)
        return state;

      let selectedItemId = (state.selectedItemIndex !== -1) ? state.selectedCategoryItems[state.selectedItemIndex].id : null;

      let updateSelectedCategory = false;
      let updateSelectedItem = false;

      let visibleCategoryIds = new Set<number>();

      items.forEach((item, index) => {
        let hidden = item.hiddenByStarredOnly || item.hiddenByFilter;

        if (!hidden) {
          for (let categoryId of item.categoryIds) {
            visibleCategoryIds.add(categoryId);
          }
        }

        if (hidden !== item.hidden) {
          if (item.id === selectedItemId) {
            updateSelectedItem = true;
          }

          items[index] = { ...item, hidden };
        }
      });

      let categories = state.categories.map((category, index) => {
        let hidden = !category.alwaysVisible && !visibleCategoryIds.has(category.id);
        if (hidden === category.hidden)
          return category;

        if (index === state.selectedCategoryIndex) {
          updateSelectedCategory = true;
        }

        return {
          ...category,
          hidden
        };
      });

      state = { ...state, items, categories };

      if (updateSelectedCategory) {
        let categoryIndex = categories.findIndex(c => !c.hidden);

        state.selectedCategoryIndex = categoryIndex;
        state.selectedCategory = categories[categoryIndex];

        if (categoryIndex !== -1) {
          updateSelectedItem = true;
        }
      }

      if (state.selectedCategory) {
        // Ensures that the currently displayed list responds to changes

        state.selectedCategoryItems = state.items.filter(item => item.categoryIds.find(id => id === state.selectedCategory.id));
      }

      if (updateSelectedItem || state.selectedItemIndex === -1) {
        state.selectedItemIndex = state.selectedCategoryItems.findIndex(i => !i.hidden);
        state.selectedItem = getSelectedItem(state);
      }

      return state;
    }),


    on(actions.setStarredOnly, (state, { starredOnly }) => {
      return {
        ...state,
        starredOnly
      };
    }),
    
    on(actions.setFilter, (state, { filter }) => {
      let normalizedFilter = filter;
      filter = isWhitespaceOnly(normalizedFilter) ? null : filter;

      return {
        ...state,
        filter
      };
    }),


    on(actions.updateItem, (state, { id, data }) => {
      state = {
        ...state,
        items: state.items.map(item => {
          if (item.id === id) {
            return {
              ...item,
              ...data
            };
          }

          return item;
        }),
      };
      
      if (state.selectedCategoryIndex !== -1) {
        // List of selected category items must be updated, to trigger change
        let categoryId = state.categories[state.selectedCategoryIndex].id;

        state.selectedCategoryItems = state.items.filter(item => item.categoryIds.find(id => id === categoryId));
      }

      if (id === state.selectedItem?.id) {
        state.selectedItem = getSelectedItem(state);
      }

      return state;
    }),
  );
}
